import type { WaitingChat } from '../connect/agentStream.js';

/**
 * Banner for a chat waiting in the queue, delivered over the realtime SSE stream
 * (server-push path — works even though Connect's CCP can't be embedded cross-site).
 * The workspace plays a looping ringtone while any waiting chat is un-acknowledged.
 *
 *  - "Open chat" acknowledges (silences this tab) and opens the chat's CRM conversation,
 *    where <ChatComposer> lets the agent reply (via the CUSTOM_BOT "Support" participant).
 *  - "Pass to queue" silences this tab only, leaving the chat in the queue for another
 *    representative to take.
 *
 * The banner disappears for everyone once the chat is accepted or ends (a `chat.cleared`
 * event), and disappears on this tab once acknowledged.
 */
export function WaitingChatAlert({
  chat,
  onOpen,
  onPass,
}: {
  chat: WaitingChat;
  onOpen: () => void;
  onPass: () => void;
}) {
  const customer = chat.customer || 'A visitor';
  return (
    <div className="incoming-alert" role="alert" aria-live="assertive">
      <span className="incoming-alert__pulse" aria-hidden="true" />
      <div className="incoming-alert__body">
        <div className="incoming-alert__title">
          <span className="badge badge--chat">Incoming chat</span>
          <strong>{customer} is waiting to chat</strong>
        </div>
        <div className="incoming-alert__meta">
          {chat.sourceDomain ? `From ${chat.sourceDomain}` : 'A customer is waiting for an agent.'}
        </div>
      </div>
      <div className="incoming-alert__actions">
        <button type="button" className="btn btn--primary" onClick={onOpen}>
          Open chat
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onPass}
          title="Silence on this device and leave the chat in the queue for another rep"
        >
          Pass to queue
        </button>
      </div>
    </div>
  );
}
